import { ThemeProvider } from '@ori-ui/mui';
import { UniversalTokenProvider, doDevelopmentLogin } from '@ori/auth';
import type { PickClientAppDataConfigProps } from '@ori/client-app-config';
import { parseClientAppConfig } from '@ori/client-app-config';
import { ServiceUrlsProvider } from '@ori/configuration-manager';
import { TranslationsProvider } from '@ori/i18n';
import { LoginProvider } from '@ori/login';
import { ErrorBoundary } from '@ori/presentation-components';
import type { AppConfig } from '@ori/top-area';
import { TopArea, defaultTranslationsWithNamespaces } from '@ori/top-area';
import '@ori/top-area/src/App.css';
import { createRoot } from 'react-dom/client';

/**
 * React app renderer that is used when app is used as a standalone app
 */
export const render = async (rootElement: HTMLElement) => {
  const { config = '' } = rootElement.dataset;
  const { customerId, identityUrl, ...clientAppConfig } = parseClientAppConfig<
    AppConfig &
      PickClientAppDataConfigProps<
        'currentLanguage' | 'customerId' | 'globalApiUrl' | 'graphqlUrl' | 'identityUrl' | 'staticApiUrl' | 'tenant'
      >
  >(config, [
    'currentLanguage',
    'globalApiUrl',
    'graphqlUrl',
    'mobileOfficeApiUrl',
    'ssoAdLoginUrl',
    'staticApiUrl',
    'tenant',
    'useSsoIdentityLogin',
  ]);
  const { currentLanguage, globalApiUrl, graphqlUrl, mobileOfficeApiUrl, staticApiUrl, tenant, useSsoIdentityLogin } =
    clientAppConfig;

  // Get token for local development
  if (process.env.NODE_ENV !== 'production') {
    // when using Requestly we do not want to load token
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      // eslint-disable-next-line no-console -- wanted behavior
      console.log('USING REQUESTLY');
    } else {
      if (!identityUrl) {
        throw new Error('Missing Identity URL!');
      }

      await doDevelopmentLogin({ identityUrl, tenant });
    }
  }

  createRoot(rootElement).render(
    <ErrorBoundary>
      <ServiceUrlsProvider
        serviceUrls={{
          globalApiUrl,
          graphQlUrl: graphqlUrl,
          identityUrl,
          mobileOfficeApiUrl,
          staticApiUrl,
        }}
      >
        <UniversalTokenProvider
          customerId={customerId}
          initialToken=""
        >
          <ThemeProvider
            enableCacheProvider
            cacheKey="top-area"
          >
            <LoginProvider isSsoIdentityLogin={useSsoIdentityLogin}>
              <TranslationsProvider
                language={currentLanguage}
                defaultTranslationsWithNamespaces={defaultTranslationsWithNamespaces}
                // eslint-disable-next-line no-console -- we need to log errors
                clientFetchConfiguration={{ onError: console.error, tenant, baseUrl: staticApiUrl }}
              >
                <TopArea configuration={clientAppConfig} />
              </TranslationsProvider>
            </LoginProvider>
          </ThemeProvider>
        </UniversalTokenProvider>
      </ServiceUrlsProvider>
    </ErrorBoundary>,
  );
};
